import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { RestrictionTypes } from 'api/Wallet/Types/RestrictionTypes'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
  WalletType,
} from 'api/Wallet/Types/Wallet'
import { WalletToTradingAccountTransfer } from 'api/Wallet/Types/WalletToTradingAccountTransfer'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { WalletToTradingAccountETDTransferForm } from './WalletToTradingAccountETDTransferForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto
  predefinedTradingAccount?: TradingAccountDto
  setClient(): void
}

const filterFromRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferTo].includes(restriction.id)
const filterToRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferFrom].includes(restriction.id)

const useFormatter = (props: {
  selectedWallet?: WalletDto
  selectedTradingAccount?: TradingAccountDto
}) => {
  const fromRestrictions = props?.selectedWallet?.restrictions.filter(filterFromRestrictions) || []
  const toRestrictions =
    props?.selectedTradingAccount?.restrictions.filter(filterToRestrictions) || []

  return {
    walletTooltipTitle: fromRestrictions?.map((restriction) => restriction.name).join(', '),
    hasWalletRestrictions: fromRestrictions?.length > 0,
    tradingAccountTooltipTitle: toRestrictions.map((restriction) => restriction.name).join(', '),
    hasTradingAccountRestrictions: toRestrictions?.length > 0,
  }
}

const WalletToToTradingAccountETDTransferPage: React.FC<Props> = ({
  preSelectedWallet,
  predefinedTradingAccount,
  client,
  setClient,
}) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const setToast = useContext(ToastContext)
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const [selectedWallet, setSelectedWallet] = useState<WalletDto>()
  const [selectedTradingAccount, setSelectedTradingAccount] = useState<TradingAccountDto>()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    const resp = await walletsApiClient.getClientWallet(client.id)
    return resp.filter((w) => w.walletType.id === WalletType.ETD)
  }, [client])
  const { data: wallets, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (preSelectedWallet) {
      onSelectWallet(preSelectedWallet.id)
    }
  }, [wallets, preSelectedWallet, predefinedTradingAccount]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (predefinedTradingAccount) {
      onSelectTradingAccount(predefinedTradingAccount.id)
    }
  }, [predefinedTradingAccount, tradingAccounts]) // eslint-disable-line react-hooks/exhaustive-deps

  const tradingAccountData = useCallback(
    (walletId: string) => tradingAccountsApiClient.getTradingAccountByWalletId(walletId),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const {
    tradingAccountTooltipTitle,
    walletTooltipTitle,
    hasWalletRestrictions,
    hasTradingAccountRestrictions,
  } = useFormatter({
    selectedWallet,
    selectedTradingAccount,
  })

  const onSelectWallet = async (walletId: string) => {
    const wallet = selectWalletById(wallets || [], walletId)
    if (wallet) {
      setLoading(true)
      const response = await tradingAccountData(wallet.id)
      setTradingAccounts(response?.items || [])
      setSelectedWallet(wallet)
      setCurrency(wallet.currency.id)
      setLoading(false)
    }
  }

  const onSelectTradingAccount = async (tradingAccountId: string) => {
    const tradingAccount = selectTradingAccountById(tradingAccounts || [], tradingAccountId)
    if (tradingAccount) {
      setSelectedTradingAccount(tradingAccount)
    }
  }

  const handleSubmit = async (values: WalletToTradingAccountTransfer) => {
    const response = await walletsApiClient.createWalletToTradingAccountTransfer(values)
    setToast({
      type: 'success',
      requireInteraction: true,
      title: `Transfer has been submitted.`,
      subtitle: 'Click for details',
      url: generatePath('/transactions/:id', {
        id: getIdFromLocationHeader(response.headers.location),
      }),
    })
    forceRefresh()
  }

  const resetAll = () => {
    setSelectedWallet(undefined)
    setSelectedTradingAccount(undefined)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <WalletToTradingAccountETDTransferForm
      isLoading={loading}
      currency={currency}
      onSelectWallet={onSelectWallet}
      onSelectTradingAccount={onSelectTradingAccount}
      client={client}
      wallets={wallets || []}
      tradingAccounts={tradingAccounts}
      setClient={setClient}
      selectedWallet={selectedWallet}
      selectedTradingAccount={selectedTradingAccount}
      tradingAccountTooltipTitle={tradingAccountTooltipTitle}
      walletTooltipTitle={walletTooltipTitle}
      hasWalletRestrictions={hasWalletRestrictions}
      hasTradingAccountRestrictions={hasTradingAccountRestrictions}
      onSubmit={handleSubmit}
      resetAll={resetAll}
    />
  )
}

const selectWalletById = (walletItems: WalletDto[], walletId: string): WalletDto | undefined => {
  return walletItems.find((x) => x.id === walletId)
}

const selectTradingAccountById = (
  tradingAccountItems: TradingAccountDto[],
  tradingAccountId: string
): TradingAccountDto | undefined => {
  return tradingAccountItems.find((x) => x.id === tradingAccountId)
}

export default WalletToToTradingAccountETDTransferPage
